import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  featureFlagsService, 
  type FeatureFlag, 
  type FeatureFlagPublic, 
  type UpdateFeatureFlagData
} from '../services/admin/featureFlagsService';

export const featureFlagsKeys = {
  all: ['feature-flags'] as const,
  public: ['feature-flags', 'public'] as const,
  admin: ['feature-flags', 'admin'] as const,
};

export function useFeatureFlagsPublic() {
  return useQuery<FeatureFlagPublic[]>({
    queryKey: featureFlagsKeys.public,
    queryFn: featureFlagsService.getPublic,
    staleTime: 5 * 60 * 1000, // 5 минут - оптимально для feature flags
    gcTime: 10 * 60 * 1000,   // 10 минут в памяти
    refetchOnWindowFocus: true, // Обновит если stale (> 5 мин)
    refetchOnMount: true,       // Обновит если stale (> 5 мин)
  });
}

/**
 * Проверить состояние конкретного feature flag
 * @param key - ключ флага
 * @returns { isEnabled, isLoading, flag }
 */
export function useFeatureFlag(key: string) {
  const { data: flags, isLoading, error } = useFeatureFlagsPublic();
  
  const flag = flags?.find(f => f.key === key);
  const isEnabled = flag?.enabled ?? false;
  
  return {
    isEnabled,
    isLoading,
    error,
    flag,
  };
}

// ============================================================================
// ADMIN ХУКИ (требуют авторизации)
// ============================================================================

export function useFeatureFlagsAdmin() {
  return useQuery<FeatureFlag[]>({
    queryKey: featureFlagsKeys.admin,
    queryFn: featureFlagsService.getAll,
    staleTime: 2 * 60 * 1000, // 2 минуты - достаточно для админки
    gcTime: 5 * 60 * 1000,    // 5 минут в памяти
  });
}

export function useUpdateFeatureFlag() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ key, data }: { key: string; data: UpdateFeatureFlagData }) =>
      featureFlagsService.update(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureFlagsKeys.admin });
      queryClient.invalidateQueries({ queryKey: featureFlagsKeys.public });
    },
  });
}


